import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Form, Spinner, Table } from 'react-bootstrap';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import { useSenhaPadrao } from '../../lib/senhaPadrao';

const NIVEL_ROTULO = { 1: 'Superadmin', 2: 'Admin', 3: 'Corretor' };

export default function ListaUsuarios() {
  const { usuario } = useAuth();
  const toast = useToast();
  const confirmar = useConfirm();
  const { senhaPadrao } = useSenhaPadrao();
  const [empresas, setEmpresas] = useState(null);
  const [empresaId, setEmpresaId] = useState('');
  const [usuarios, setUsuarios] = useState(null);
  const [erro, setErro] = useState(false);
  const [recarregar, setRecarregar] = useState(0); // incrementa depois de excluir → refaz a busca

  // Excluir usuário (a API já existia; faltava o botão). O backend recusa: apagar a si
  // mesmo, apagar o ÚLTIMO superadmin e, pro admin da imobiliária (nivel 2), apagar
  // gente de outra empresa — a mensagem dele vira o toast de erro.
  async function excluir(u) {
    const aviso =
      u.nivel === 2
        ? `Excluir o admin "${u.name}"? Ele perde o acesso ao painel. Esta ação não pode ser desfeita.`
        : `Excluir "${u.name}"? Esta ação não pode ser desfeita.`;
    if (!(await confirmar({ titulo: 'Excluir usuário', mensagem: aviso, variante: 'perigo', textoConfirmar: 'Excluir' }))) return;
    try {
      await api.delete(`/usuarios/${u.public_id}`);
      toast.sucesso('Usuário excluído com sucesso.');
      setRecarregar((n) => n + 1);
    } catch (err) {
      toast.erro(err.message || 'Não foi possível excluir o usuário');
    }
  }

  // Volta a senha do usuário pro padrão ("123mudar"): ele troca no próximo acesso. Serve quando o
  // e-mail dele não chega. O servidor recusa fora da empresa e pra si mesmo.
  async function redefinirSenha(u) {
    if (!(await confirmar(`Redefinir a senha de "${u.name}" para ${senhaPadrao}? Ele será obrigado a trocar no próximo acesso.`))) return;
    try {
      await api.post(`/usuarios/${u.public_id}/redefinir-senha`);
      toast.sucesso(`Senha de ${u.name} redefinida para ${senhaPadrao}.`);
    } catch (err) {
      toast.erro(err.message || 'Não foi possível redefinir a senha');
    }
  }

  // Nivel 1 pode filtrar por empresa (seletor abaixo) — nivel 2 nem vê
  // esse seletor, a API já restringe à própria empresa sozinha.
  useEffect(() => {
    if (usuario.nivel === 1) {
      api.get('/empresas').then((r) => setEmpresas(r.data));
    }
  }, [usuario.nivel]);

  useEffect(() => {
    // Achado em auditoria 2026-09-18, dois problemas:
    // 1) sem guarda de resposta obsoleta — trocar de empresa rápido (A
    //    depois B) podia deixar a resposta de A "vencer" se chegasse
    //    depois da de B, mostrando a lista errada com o seletor já em B.
    // 2) `erro` só era setado, nunca limpo — uma falha antiga continuava
    //    visível mesmo depois de uma troca de empresa bem-sucedida.
    // Achado em auditoria 2026-09-19 (análise do Laravel legado): nivel 1
    // SEM filtro agora é modo GLOBAL (todos os usuários de todas as
    // empresas, inclusive outros superadmins) — antes obrigava escolher
    // uma empresa antes de ver qualquer usuário.
    let cancelado = false;
    setErro(false);
    const query = usuario.nivel === 1 && empresaId ? `?empresa_id=${empresaId}` : '';
    api
      .get(`/usuarios${query}`)
      .then((r) => {
        if (!cancelado) setUsuarios(r.data);
      })
      .catch((err) => {
        if (!cancelado) {
          setErro(true);
          toast.erro(err.message || 'Não foi possível carregar os usuários');
        }
      });
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario.nivel, empresaId, recarregar]);

  const modoGlobal = usuario.nivel === 1 && !empresaId;
  // "Gerente de equipe" (nivel 3 com `usuarios_ver`, 22/10/2026) só VÊ a lista — gerenciar
  // (criar/editar/redefinir senha/excluir) continua exclusivo de nivel 1/2, igual sempre foi.
  const podeGerenciar = usuario.nivel === 1 || usuario.nivel === 2;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
        <h1 className="h4 mb-0">Usuários</h1>
        {podeGerenciar && (
          <Button
            as={Link}
            to={usuario.nivel === 1 && empresaId ? `/usuarios/novo?empresa_id=${empresaId}` : '/usuarios/novo'}
            variant="primary"
          >
            Novo usuário
          </Button>
        )}
      </div>

      {usuario.nivel === 1 && (
        <Form.Group className="mb-3" style={{ maxWidth: 350 }}>
          <Form.Label>Empresa</Form.Label>
          <Form.Select value={empresaId} onChange={(e) => setEmpresaId(e.target.value)}>
            <option value="">Todas as empresas</option>
            {(empresas || []).map((emp) => (
              <option key={emp.public_id} value={emp.public_id}>
                {emp.e1_nome}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      )}

      {!usuarios && !erro && <Spinner animation="border" />}

      {usuarios && (
        <Table hover responsive>
          <thead>
            <tr>
              <th>Nome</th>
              <th>E-mail</th>
              {modoGlobal && <th>Empresa</th>}
              <th>Nível</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {usuarios.map((u) => (
              <tr key={u.public_id}>
                <td>{u.name}</td>
                <td>{u.email}</td>
                {modoGlobal && <td>{u.empresa_nome || '—'}</td>}
                <td>{NIVEL_ROTULO[u.nivel] || u.nivel}</td>
                <td>
                  {u.bloqueio === 'true' ? (
                    <Badge bg="danger">Bloqueado</Badge>
                  ) : (
                    <Badge bg="success">Ativo</Badge>
                  )}
                </td>
                <td className="text-end text-nowrap">
                  <Button as={Link} to={`/usuarios/${u.public_id}`} size="sm" variant="outline-secondary">
                    {podeGerenciar ? 'Editar' : 'Ver'}
                  </Button>
                  {/* o próprio usuário logado não se exclui (o backend também recusa); gerente de
                      equipe (só visualização) nunca vê esses dois botões */}
                  {podeGerenciar && u.public_id !== usuario.id && (
                    <>
                      <Button size="sm" variant="outline-warning" className="ms-1" onClick={() => redefinirSenha(u)}>
                        Redefinir senha
                      </Button>
                      <Button size="sm" variant="outline-danger" className="ms-1" onClick={() => excluir(u)}>
                        Excluir
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {usuarios.length === 0 && (
              <tr>
                <td colSpan={modoGlobal ? 6 : 5} className="text-muted">
                  Nenhum usuário cadastrado ainda.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
    </>
  );
}
